/**
 * <s-gallery-nav> — mobile prev/next arrows and a position counter for the
 * horizontally scrolling product gallery.
 *
 * Required markup (inside the same parent as the gallery):
 *   [data-product-gallery]          — the scroll-snap track (sibling of this element).
 *   [data-gallery-prev] / [data-gallery-next] — buttons inside this element.
 *   [data-gallery-counter]          — text node target; data-template holds the
 *                                     translated "[current] / [total]" string.
 */
class SGalleryNav extends HTMLElement {
  #ac;
  #track;
  #raf = 0;

  connectedCallback() {
    this.#track = this.parentElement?.querySelector('[data-product-gallery]');
    if (!this.#track) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#go(-1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#go(1), opts);
    this.#track.addEventListener('scroll', this.#scheduleUpdate, { ...opts, passive: true });
    window.addEventListener('resize', this.#scheduleUpdate, opts);

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  #slides() {
    return [...this.#track.children].filter((el) => el.offsetWidth > 0);
  }

  #currentIndex(slides) {
    const trackLeft = this.#track.getBoundingClientRect().left;
    let best = 0;
    let bestDistance = Infinity;
    slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #go(step) {
    const slides = this.#slides();
    if (slides.length < 2) return;
    const target = Math.min(Math.max(this.#currentIndex(slides) + step, 0), slides.length - 1);
    const delta = slides[target].getBoundingClientRect().left - this.#track.getBoundingClientRect().left;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollBy({ left: delta, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #scheduleUpdate = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const slides = this.#slides();
    if (slides.length < 2) {
      this.hidden = true;
      return;
    }
    this.hidden = false;

    const index = this.#currentIndex(slides);
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index === slides.length - 1;

    const counter = this.querySelector('[data-gallery-counter]');
    const template = counter?.dataset.template;
    if (counter && template) {
      const text = template.replace('[current]', String(index + 1)).replace('[total]', String(slides.length));
      if (counter.textContent !== text) counter.textContent = text;
    }
  }
}

if (!customElements.get('s-gallery-nav')) {
  customElements.define('s-gallery-nav', SGalleryNav);
}
